import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { Bem } from '../../service/Bem/Bem';
import { formatNumber, parseNumber } from '../../service/FormatHelpers/FormatHelpers';
import { getTitleForDayBudgetMode } from '../../service/TextStoreHelpers/TextStoreHelpers';
import { RawDayBudgetValue } from '../../typings/budget';
import { CurrencyCode, GdCampaignBudgetShowMode } from '../../typings/graphql-grid';
import { ErrorItemText } from '../ErrorsPresenter/ErrorsPresenter';
import { SelectDecorator } from '../SelectDecorator/SelectDecorator';
import { TextInputDecorator } from '../TextInputDecorator/TextInputDecorator';
import { AdditionalTumblerTone, TumblerDecorator } from '../TumblerDecorator/TumblerDecorator';

import './DayBudgetEditor.css';
import * as keyset from './DayBudgetEditor.i18n';

const i18n = i18nFactory(keyset);

export interface DayBudgetEditorProps {
    value: RawDayBudgetValue;
    currency: CurrencyCode;
    onChange: (value: RawDayBudgetValue) => void;
    error?: ErrorItemText;
}

export class DayBudgetEditor extends React.Component<DayBudgetEditorProps> {
    render() {
        const { value, currency, error } = this.props;
        let errorContent;

        if (error !== undefined) {
            errorContent = <div className={Bem.cls('day-budget-editor', 'errors')}>{error}</div>;
        }

        return (
            <div className={Bem.cls('day-budget-editor')}>
                <div className={Bem.cls('day-budget-editor', 'controls')}>
                    <TumblerDecorator
                        cls={Bem.cls('day-budget-editor', 'switcher')}
                        theme="normal"
                        view="default"
                        tone={AdditionalTumblerTone.direct}
                        size={'n'}
                        checked={value.isActive}
                        onChange={this.onActiveStateChange}
                    />

                    <div className={Bem.cls('day-budget-editor', 'sum')}>
                        <TextInputDecorator
                            text={value.sum}
                            theme={'normal'}
                            view={'classic'}
                            valid={!error}
                            size={'s'}
                            cls={Bem.cls('day-budget-editor', 'sum-input')}
                            onBlur={this.onSumFieldBlur}
                            onChange={this.onSumChange}
                            disabled={!value.isActive}
                        />{' '}
                        <span className={Bem.cls('day-budget-editor', 'text')}>
                            {mapStringToCurrencySymbol(currency)} {i18n('perDay')}
                        </span>
                    </div>

                    <div className={Bem.cls('day-budget-editor', 'mode')}>
                        <span className={Bem.cls('day-budget-editor', 'text')}>{i18n('dayBudgetMode')}</span>

                        <SelectDecorator
                            popup={{
                                hiding: false
                            }}
                            items={this.getModeSelectItems()}
                            view={'classic'}
                            theme={'normal'}
                            size={'s'}
                            type={'radio'}
                            cls={Bem.cls('day-budget-editor', 'mode-select')}
                            val={value.mode}
                            disabled={!value.isActive}
                            onChange={this.onModeChange}
                            button={{
                                view: 'classic'
                            }}
                        />
                    </div>
                </div>

                {errorContent}
            </div>
        );
    }

    private getModeSelectItems = () => {
        return Object.keys(GdCampaignBudgetShowMode).map(mode => ({
            val: GdCampaignBudgetShowMode[mode],
            text: getTitleForDayBudgetMode(GdCampaignBudgetShowMode[mode])
        }));
    };

    private onActiveStateChange = () => {
        const currentValue = this.props.value,
            isActive = !this.props.value.isActive;

        this.props.onChange({
            ...currentValue,
            isActive: isActive,
            sum: String(isActive ? currentValue.sum : 0),
            mode: isActive ? currentValue.mode : GdCampaignBudgetShowMode.DEFAULT
        });
    };

    private formatSum = (sum: number): string => {
        return sum ? formatNumber(sum, 2) : '';
    };

    private onSumChange = (sum: string) => {
        this.props.onChange({
            ...this.props.value,
            sum
        });
    };

    private onSumFieldBlur = () => {
        const sum = parseNumber(this.props.value.sum);
        const sumValue = isNaN(sum) ? 0 : sum;
        this.props.onChange({
            ...this.props.value,
            sum: this.formatSum(sumValue)
        });
    };

    private onModeChange = (mode: GdCampaignBudgetShowMode[]) => {
        this.props.onChange({
            ...this.props.value,
            mode: mode[0]
        });
    };
}
